import { useQuery } from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type { CollectionGrid } from './use-structure-grid-collections';
import type { SectionVertex } from './types';

/** One cache entry per line-and-collection pair; the answer is small. */
export const INTERSECTING_GRIDS_QUERY_KEY =
  'cross-section:grids-intersecting' as const;

/** How long an answer stays fresh, in ms. Grids change far more slowly than this. */
const STALE_MS = 300_000;

/** A grid as the grids query returns it, before it is read as a stack row. */
interface GridRow {
  id: number;
  href: string;
  geologicInterval: string | null;
  geologicIntervalPositionalIndex: number | null;
  geologicIntervalDisplayColor: string | null;
  gridSurface: string;
  owner: string | null;
  source: string | null;
  validity: string;
  avgThickness: number | null;
  basinName: string | null;
  createdAt: string | null;
  cellWidthFeet: number | null;
  cellHeightFeet: number | null;
  /**
   * Every collection holding this grid. A grid can sit in several, and crossing it makes
   * all of them collections the section passes through — which is what the dropdown
   * reads to tell a stack with something to say about this line from one without.
   */
  collectionIds: number[];
}

interface GridsResponse {
  totalCount: number;
  results: GridRow[];
}

/** What the picker shows where a grid has no value for a column. */
const MISSING = '--';

/**
 * A grid row in the shape the settings panel and the picker already read.
 *
 * @param {GridRow} row - The grid as the query returned it.
 * @return {CollectionGrid} The grid as the stack reads it.
 */
const asStackRow = (row: GridRow): CollectionGrid => ({
  href: row.href,
  structureGridId: row.id,
  gridSurface: row.gridSurface,
  // An intervalless grid is a real record; it just has nothing to say in that column.
  geologicInterval: row.geologicInterval ?? MISSING,
  geologicIntervalPositionalIndex: row.geologicIntervalPositionalIndex,
  geologicIntervalDisplayColor: row.geologicIntervalDisplayColor,
  source: row.source,
  owner: row.owner,
  validity: row.validity,
  avgThickness: row.avgThickness,
  basinName: row.basinName,
  cellWidthFeet: row.cellWidthFeet,
  cellHeightFeet: row.cellHeightFeet,
  createdAt: row.createdAt,
});

/**
 * Every structure grid the drawn section crosses, whatever collection it belongs to.
 *
 * A collection is a curated stack, not the limit of what a line passes through: the
 * picker offers any grid the section would actually sample, which is why the backend
 * answers this with the same predicate the horizon sampler keeps a tile by. Asked over
 * the section's own geometry rather than a bounding box, so a long dog-legged line
 * does not drag in grids it misses.
 *
 * The collection rides along as context rather than as a filter — average thickness is
 * a property of a grid's place in a collection's ordering, so a grid outside the
 * selected one simply has none.
 *
 * @param {Array} vertices - The section's vertices, in along-line order.
 * @param {number | null} collectionId - The selected collection, for thickness.
 * @param {boolean} enabled - Whether to ask at all; false while the picker is shut.
 * @return {object} The grids, load state, and any error.
 */
export const useIntersectingStructureGrids = (
  vertices: SectionVertex[],
  collectionId: number | null,
  enabled: boolean
) => {
  // A line needs two points. Rounded into the key so a drag that moves a vertex by
  // inches does not invalidate the answer.
  const coordinates = vertices.map(({ lat, lon }) => [
    Number(lon.toFixed(5)),
    Number(lat.toFixed(5)),
  ]);
  const askable = enabled && coordinates.length >= 2;

  const { data, isFetching, error } = useQuery<GridsResponse, Error>({
    queryKey: [INTERSECTING_GRIDS_QUERY_KEY, coordinates, collectionId],
    queryFn: ({ signal }) =>
      api
        .post('queries/structure_grids', {
          // `wrap_parameters :query` on the controller, so the body nests under `query`.
          json: {
            query: {
              paginated: false,
              intersecting_geometry: {
                type: 'LineString',
                coordinates,
              },
              ...(collectionId === null
                ? {}
                : { structure_grid_collection_id: collectionId }),
            },
          },
          signal,
        })
        .json<GridsResponse>(),
    enabled: askable,
    staleTime: STALE_MS,
  });

  return {
    grids: (data?.results ?? []).map(asStackRow),
    /**
     * The collections the section passes through, derived from the crossed grids rather
     * than asked for separately. Empty until the answer lands, which is why callers gate
     * on `answered` rather than on the size of this.
     */
    intersectingCollectionIds: new Set(
      (data?.results ?? []).flatMap((row) => row.collectionIds)
    ),
    /**
     * Whether the line has been asked about and answered. Distinct from an empty
     * answer: a line that crosses no grid has nothing to offer, where a line not yet
     * asked about falls back to the collection's own stack.
     */
    answered: askable && data !== undefined,
    /**
     * Asked but not yet answered. The picker's door is held open on this: a line that
     * MIGHT cross a grid the collection lacks must not be shut out on the collection's
     * word alone. A section with no line to ask about is not pending.
     */
    pending: askable && data === undefined,
    loading: askable && isFetching,
    error: error ?? null,
  };
};
